'use client';

import { auth } from '@/lib/firebase';
import * as UserFactory from '@/services/user';
import {
  Button,
  Field,
  Heading,
  Icon,
  Input,
  Label,
  Logo,
  Text,
  Translation,
} from '@/ui/components';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useFormik } from 'formik';
import { useLocale } from 'next-intl';
import { useState } from 'react';
import * as yup from 'yup';
import { ResetPasswordSent } from '../components';

import s from './page.module.css';

export default function ResetPage() {
  const locale = useLocale();
  const [sent, setSent] = useState(false);
  const [hasError, setHasError] = useState(false);
  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: yup.object().shape({
      email: yup
        .string()
        .email('Please enter a valid email address')
        .required('Email address is required'),
    }),
    onSubmit: async ({ email }) => {
      try {
        await UserFactory.forgotPassword(email);

        await sendPasswordResetEmail(auth, email, {
          url: `${window.location.origin}/${locale}/account/new-password?email=${email}`,
        });

        setSent(true);
      } catch (err) {
        setHasError(true);
      }
    },
  });

  const handleFocusEmail = () => {
    if (hasError) {
      formik.setFieldTouched('email', false, true);
      setHasError(false);
    }
  };

  if (sent) return <ResetPasswordSent />;

  return (
    <section className={s.page}>
      <Logo />
      <Heading variant="large-title" weight="medium" centerText spacing="tiny">
        <Translation context="reset_password" token="title" />
      </Heading>
      <Text centerText spacing="semi">
        <Translation context="reset_password" token="description" />
      </Text>
      <form className={s.form} onSubmit={formik.handleSubmit}>
        <Field spacing="medium">
          <Label spacing="micro" htmlFor="email">
            <Translation context="reset_password" token="email_label" />
          </Label>
          <Input
            id="email"
            name="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            onFocus={handleFocusEmail}
            hasError={formik.touched.email && !!formik.errors.email}
            errorMessage={formik.errors.email}
          />
        </Field>
        <footer className={s.footer}>
          <Button isFullWidth type="submit" disabled={formik.isSubmitting}>
            <Translation context="reset_password" token="send_button" />
          </Button>
          <Button
            isFullWidth
            variant="link"
            icon={<Icon variant="solid" name="ChevronLeft" />}
            href={`/account/login`}
          >
            <Translation context="reset_password" token="back_button" />
          </Button>
        </footer>
      </form>
    </section>
  );
}
